// lib/lead-request.ts
//
// Request-shape and redirect helpers for POST /api/leads.
//
// ── Why this exists ────────────────────────────────────────────────────────
// The JS client posts JSON and needs a JSON { ok: true } (or a JSON error)
// so LeadCapture can show success or a visible failure. The no-JS <form>
// posts application/x-www-form-urlencoded and needs a 303 back to the page
// that submitted it, with ?subscribed=1 or ?leadError=1.
//
// Two production defects hid behind "the upsert looks fine":
//
// 1. Content-type defaulted to "form" whenever the header was missing or
//    did not contain the substring "application/json". A JSON client in
//    that state received a 303. fetch() follows 303 by default; the
//    followed GET is 200 HTML, so LeadCapture treated a failed (or
//    never-parsed) write as success.
//
// 2. redirectBack() built the 303 Location from req.nextUrl.origin.
//    On Cloud Run that origin is the container listen address
//    (https://0.0.0.0:8080) — the same class as #1314. A live probe on
//    2026-08-19 returned Location: https://0.0.0.0:8080/#subscribed=1.
//    The row was written; the visitor never saw confirmation.
//
// Form-encoded is now opt-in. Redirects use the public host (X-Forwarded-Host
// / Host, with the Cloud Run listen port stripped), never nextUrl.origin.

import type { NextRequest } from "next/server";

const CANONICAL_ORIGIN = "https://www.finders-forge.com";
const CONTAINER_HOSTS = new Set(["0.0.0.0", "127.0.0.1", "::", "[::]"]);

/**
 * Status implied by a 303 return URL. Used by LeadCapture via
 * useSyncExternalStore so the no-JS confirmation is visible without
 * calling setState inside an effect (react-hooks/set-state-in-effect).
 */
export function leadReturnStatusFromHref(
  href: string,
): "success" | "error" | null {
  try {
    const url = new URL(href, CANONICAL_ORIGIN);
    const hash = url.hash.replace(/^#/, "");
    if (url.searchParams.get("subscribed") === "1" || hash === "subscribed=1") {
      return "success";
    }
    if (url.searchParams.get("leadError") === "1" || hash === "leadError=1") {
      return "error";
    }
  } catch {
    return null;
  }
  return null;
}

export function isFormEncodedLeadRequest(req: NextRequest): boolean {
  const contentType = req.headers.get("content-type") ?? "";
  return (
    contentType.includes("application/x-www-form-urlencoded") ||
    contentType.includes("multipart/form-data")
  );
}

/**
 * Public origin for a 303 back to the page that submitted the form.
 *
 * Never returns a container bind address. :8080 is Cloud Run's listen port
 * and is stripped when it appears on Host / X-Forwarded-Host (#1314).
 */
export function publicRequestOrigin(req: NextRequest): string {
  const forwardedHost = req.headers.get("x-forwarded-host")?.split(",")[0]?.trim();
  const hostHeader = req.headers.get("host")?.split(",")[0]?.trim();
  const host = stripListenPort(forwardedHost || hostHeader || "");

  if (host && !isContainerHost(host)) {
    const proto = (
      req.headers.get("x-forwarded-proto") ?? "https"
    )
      .split(",")[0]!
      .trim();
    return `${proto}://${host}`;
  }

  const fromEnv = (
    process.env.NEXT_PUBLIC_SITE_URL ||
    process.env.BETTER_AUTH_URL ||
    CANONICAL_ORIGIN
  ).replace(/\/$/, "");
  try {
    const url = new URL(fromEnv);
    if (!isContainerHost(url.host)) return url.origin;
  } catch {
    // fall through
  }
  return CANONICAL_ORIGIN;
}

export function leadCaptureRedirectUrl(
  req: NextRequest,
  flag: "subscribed" | "leadError",
): string {
  const origin = publicRequestOrigin(req);
  const target = trustedReturnUrl(req, origin);
  // The Referer is the previous return URL on a retry, so it may already
  // carry the other flag. Clear both before setting, or a failed-then-
  // successful no-JS submit lands on ?leadError=1&subscribed=1.
  target.searchParams.delete("subscribed");
  target.searchParams.delete("leadError");
  target.searchParams.set(flag, "1");
  target.hash = "";
  return target.toString();
}

function stripListenPort(host: string): string {
  return host.replace(/:8080$/, "");
}

function isContainerHost(host: string): boolean {
  const hostname = host.replace(/^\[/, "").replace(/\](?::\d+)?$/, "").split(":")[0] ?? "";
  return CONTAINER_HOSTS.has(host) || CONTAINER_HOSTS.has(hostname);
}

function trustedReturnUrl(req: NextRequest, origin: string): URL {
  const fallback = new URL("/", origin);
  const referer = req.headers.get("referer");
  if (!referer) return fallback;

  try {
    const ref = new URL(referer);
    if (!isTrustedLeadReferer(ref, origin)) return fallback;
    return ref;
  } catch {
    return fallback;
  }
}

function isTrustedLeadReferer(ref: URL, publicOrigin: string): boolean {
  try {
    const pub = new URL(publicOrigin);
    if (ref.origin === pub.origin) return true;

    const refBare = ref.hostname.replace(/^www\./, "");
    const pubBare = pub.hostname.replace(/^www\./, "");
    if (refBare === pubBare) return true;

    if (ref.hostname.endsWith(".run.app") && pub.hostname.endsWith(".run.app")) {
      return true;
    }

    return ref.hostname === "localhost" || ref.hostname === "127.0.0.1";
  } catch {
    return false;
  }
}
